import Phaser from "phaser";
import { getAgritechText, getAgritechLanguage } from "../utils/agritechText";

export default class TutorialScene extends Phaser.Scene {
  constructor() {
    super("TutorialScene");
  }

  preload() {
    this.load.image("tutorialBG", "/assets/tutorial.png");

    // NEW MOVEMENT SPRITES
    this.load.image("walk0", "/assets/movement/walk0.png");
    this.load.image("walk1", "/assets/movement/walk1.png");
    this.load.image("walk2", "/assets/movement/walk2.png");
    this.load.image("walk3", "/assets/movement/walk3.png");
    this.load.image("walk4", "/assets/movement/walk4.png");
    this.load.image("walk5", "/assets/movement/walk5.png");
    this.load.image("walk6", "/assets/movement/walk6.png");
    this.load.image("walk7", "/assets/movement/walk7.png");

    this.load.image("run1", "/assets/movement/run1.png");
    this.load.image("run2", "/assets/movement/run2.png");
    this.load.image("run3", "/assets/movement/run3.png");
    this.load.image("run4", "/assets/movement/run4.png");
    this.load.image("run5", "/assets/movement/run5.png");

    this.load.image("dive1", "/assets/movement/dive1.png");
    this.load.image("dive2", "/assets/movement/dive2.png");
    this.load.image("dive3", "/assets/movement/dive3.png");
    this.load.image("dive4", "/assets/movement/dive4.png");
    this.load.image("dive5", "/assets/movement/dive5.png");

    this.load.image("jump1", "/assets/movement/jump1.png");
    this.load.image("jump2", "/assets/movement/jump2.png");

    this.load.image("crouchFinal", "/assets/movement/crouchFinal.png");
    this.load.image("up", "/assets/movement/up.png");

    this.load.image("jumpup", "/assets/movement/jumpup.png");
    this.load.image("pound1", "/assets/movement/pound1.png");

    this.load.image("greendrop", "/assets/greendrop.png");
    this.load.image("bluedrop", "/assets/bluedrop.png");
    this.load.audio("mudSplash2", "/assets/mudSplash2.wav");

    this.load.image("spring1", "/assets/Spring1.png");
    this.load.image("spring2", "/assets/Spring2.png");
    this.load.image("spring3", "/assets/Spring3.png");

    this.load.audio("tap", "/assets/tap.m4a");
    this.load.audio("selected", "/assets/selected.m4a");
    this.load.audio("springSound", "/assets/spring.mp3");
    this.load.audio("blueSound", "/assets/bluesound.wav");
    this.load.audio("greenSound", "/assets/greensound.wav");
    this.load.audio("correct", "/assets/correct.mp3");
    this.load.audio("incorrect", "/assets/incorrect.mp3");
    this.load.audio("powerGrab", "/assets/powerGrab.wav");
    this.load.audio("apply", "/assets/apply.wav");
    this.load.audio("vacuumLoop", "/assets/effect1.wav");
    this.load.audio("thunderclap", "/assets/thunderclap.mp3");
    this.load.audio("bounceClip", "/assets/BouncePlayer.mp3");
    this.load.audio("tutorialMusic", "/assets/tutorial.mp3");

    this.load.image("star", "/assets/star.png");
    this.load.image("vacEffect", "/assets/vacEffect.png");
  }

  create() {
    this.add.image(400, 300, "tutorialBG").setDisplaySize(800, 600);
    this.t = getAgritechText();
    this.lang = getAgritechLanguage();

    this.isMobileTutorial = window.matchMedia(
      "(any-pointer: coarse), (hover: none)",
    ).matches;

    document.getElementById("mobile-tutorial-instructions")?.remove();

    this.mobileTutorialInstructions = document.createElement("div");
    this.mobileTutorialInstructions.id = "mobile-tutorial-instructions";

    this.mountMobileTutorialInstructions = () => {
      const fullscreenHost = document.fullscreenElement;
      const target =
        fullscreenHost && typeof fullscreenHost.appendChild === "function"
          ? fullscreenHost
          : document.body;

      if (this.mobileTutorialInstructions.parentElement !== target) {
        target.appendChild(this.mobileTutorialInstructions);
      }
    };

    this.handleTutorialFullscreenChange = () => {
      this.mountMobileTutorialInstructions();
    };

    this.mountMobileTutorialInstructions();

    document.addEventListener(
      "fullscreenchange",
      this.handleTutorialFullscreenChange,
    );

    this.events.once("shutdown", () => {
      document.removeEventListener(
        "fullscreenchange",
        this.handleTutorialFullscreenChange,
      );

      this.mobileTutorialInstructions?.remove();
    });

    this.getEffectsVolume = () =>
      Number(localStorage.getItem("agritech-effects-volume") ?? "0.6");

    this.tutorialMusic = this.sound.add("tutorialMusic", {
      loop: true,
      volume: Number(localStorage.getItem("agritech-music-volume") ?? "0.4"),
    });

    this.tutorialMusic.play();
    this.stormOverlay = this.add
      .rectangle(400, 300, 800, 600, 0x444488, 0)
      .setDepth(999)
      .setScrollFactor(0);

    this.platforms = this.physics.add.staticGroup();

    this.tutorialPlatform = this.platforms
      .create(400, 540, null)
      .setDisplaySize(800, 100)
      .setVisible(false)
      .refreshBody();

    this.tutorialLegacySpriteScale = 0.1;
    this.tutorialNewSpriteScale = 0.7;
    this.tutorialCrouchSpriteScale = 0.65;

    this.tutorialPlayer = this.physics.add
      .sprite(120, 350, "walk0")
      .setFlipX(true)
      .setDepth(3);

    this.tutorialPlayer.setCollideWorldBounds(true);
    this.syncTutorialPlayerSpriteSize();

    this.events.on(
      Phaser.Scenes.Events.POST_UPDATE,
      this.syncTutorialPlayerSpriteSize,
      this,
    );

    this.events.once("shutdown", () => {
      this.events.off(
        Phaser.Scenes.Events.POST_UPDATE,
        this.syncTutorialPlayerSpriteSize,
        this,
      );
    });

    // 🌱 PLANT HITBOX
    this.plant = this.physics.add.image(0, 0, null);
    this.plant.setVisible(false);
    this.plant.setDepth(20);

    this.plant.setDisplaySize(20, 20);
    this.plant.body.setSize(20, 20);
    this.plant.body.setAllowGravity(false);
    this.plant.body.setImmovable(true);

    // Visible outline for the plant's 20 × 20 hitbox.
    this.plantHitboxDebug = this.add
      .rectangle(0, 0, 20, 20, 0xff0000, 0.18)
      .setStrokeStyle(2, 0xff0000, 1)
      .setDepth(2000)
      .setVisible(false);

    this.physics.add.collider(this.tutorialPlayer, this.platforms);

    this.mobileControls = {
      up: false,
      left: false,
      down: false,
      right: false,
      z: false,
      x: false,
      one: false,
      two: false,
      three: false,
      p: false,
    };

    this.mobileJustPressed = new Set();
    this.mobileJustReleased = new Set();

    this.consumeMobilePress = (control) => {
      if (!this.mobileJustPressed.has(control)) return false;
      this.mobileJustPressed.delete(control);
      return true;
    };

    this.consumeMobileRelease = (control) => {
      if (!this.mobileJustReleased.has(control)) return false;
      this.mobileJustReleased.delete(control);
      return true;
    };

    const makeMobileAwareKey = (keyboardKey, mobileControl) =>
      new Proxy(keyboardKey, {
        get: (target, property) => {
          if (property === "isDown") {
            return target.isDown || this.mobileControls[mobileControl];
          }

          return Reflect.get(target, property, target);
        },
      });

    const keyboardCursors = this.input.keyboard.createCursorKeys();

    this.cursors = {
      up: makeMobileAwareKey(keyboardCursors.up, "up"),
      left: makeMobileAwareKey(keyboardCursors.left, "left"),
      down: makeMobileAwareKey(keyboardCursors.down, "down"),
      right: makeMobileAwareKey(keyboardCursors.right, "right"),
    };

    const keyboardJumpKey = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.Z,
    );

    const keyboardDiveKey = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.X,
    );

    this.jumpKey = makeMobileAwareKey(keyboardJumpKey, "z");
    this.diveKey = makeMobileAwareKey(keyboardDiveKey, "x");
    this.power1Key = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.ONE,
    );
    this.power2Key = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.TWO,
    );
    this.power3Key = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.THREE,
    );

    this.handleMobileControl = (event) => {
      const control = event.detail?.control;
      const pressed = event.detail?.pressed === true;

      if (!Object.prototype.hasOwnProperty.call(this.mobileControls, control)) {
        return;
      }

      const wasDown = this.mobileControls[control];

      if (wasDown === pressed) return;

      this.mobileControls[control] = pressed;

      if (control === "p") {
        if (
          pressed &&
          !wasDown &&
          !this.tutorialModalActive &&
          this.tutorialStep !== "powerQuestion" &&
          typeof this.toggleManualPause === "function"
        ) {
          this.toggleManualPause();
        }

        return;
      }

      if (
        pressed &&
        !this.manualPaused &&
        this.tutorialStep === "activatePower" &&
        ["one", "two", "three"].includes(control)
      ) {
        this.mobileJustPressed.add(control);
      }

      if (!pressed && control === "z") {
        this.mobileJustReleased.add("z");
      }
    };

    window.addEventListener("biorev-mobile-control", this.handleMobileControl);

    this.events.once("shutdown", () => {
      window.removeEventListener(
        "biorev-mobile-control",
        this.handleMobileControl,
      );
    });

    this.manualPaused = false;
    this.tutorialModalActive = false;

    this.pauseOverlay = this.add
      .rectangle(400, 300, 800, 600, 0x06130c, 0.82)
      .setDepth(5000)
      .setScrollFactor(0)
      .setInteractive()
      .setVisible(false);

    this.pauseText = this.add
      .text(400, 270, this.lang === "es" ? "PAUSA" : "PAUSE", {
        fontFamily: '"Russo One", sans-serif',
        fontSize: "64px",
        color: "#ecfccb",
        stroke: "#14532d",
        strokeThickness: 5,
        letterSpacing: 4,
      })
      .setOrigin(0.5)
      .setDepth(5001)
      .setScrollFactor(0)
      .setVisible(false);

    this.pauseHint = this.add
      .text(
        400,
        335,
        this.lang === "es"
          ? "Presiona P para continuar"
          : "Press P to continue",
        {
          fontSize: "18px",
          fontFamily: '"Montserrat", sans-serif',
          color: "#ffffff",
        },
      )
      .setOrigin(0.5)
      .setDepth(5001)
      .setScrollFactor(0)
      .setVisible(false);

    this.input.keyboard.on("keydown-P", () => {
      if (this.tutorialModalActive || this.tutorialStep === "powerQuestion") {
        return;
      }

      this.toggleManualPause();
    });

    this.events.once("shutdown", () => {
      if (!this.input?.keyboard) return;

      this.input.keyboard.clearCaptures();
      this.input.keyboard.removeAllListeners();
      this.input.keyboard.removeAllKeys(true);
    });

    this.isJumping = false;
    this.isDiving = false;
    this.isPounding = false;
    this.springActive = false;
    this.springLock = false;
    this.springNoPoundUntil = 0;
    this.tutorialSpringLaunched = false;
    this.tutorialSpringDiveDistance = 0;
    this.tutorialSpringPreviousY = 0;
    this.tutorialSpringDiveTarget = 80;

    this.tutorialStep = "intro";
    this.fadedTutorialInstructions = new Set();
    this.tutorialCanMoveRight = false;
    this.tutorialStartX = this.tutorialPlayer.x;
    this.tutorialMoveTarget = 180;
    this.tutorialCrouchTarget = 45;
    this.tutorialUpTarget = 45;
    this.tutorialHoldTimer = 0;

    this.vacuumActive = false;
    this.vacuumRadius = 250;
    this.vacuumStrength = 2500;
    this.tripleRainActive = false;
    this.bounceActive = false;
    this.bounceLock = false;
    this.wasOnGround = false;
    this.wasFalling = false;
    this.tutorialCurrentPower = "vacuum";

    this.storedPowers = {
      vacuum: false,
      storm: false,
      bounce: false,
    };

    this.tutorialPowerHud = null;
    this.tutorialPowerHudTimerEvent = null;

    this.vacuumParticles = this.add.particles(0, 0, "vacEffect", {
      speed: { min: 100, max: 200 },
      scale: { start: 0.1, end: 0 },
      lifespan: 500,
      quantity: 3,
      emitZone: {
        type: "edge",
        source: new Phaser.Geom.Circle(0, 0, 60),
        quantity: 16,
      },
      moveToX: 0,
      moveToY: 0,
    });

    this.vacuumParticles.stop();
    this.setTutorialControls([]);

    if (this.anims.exists("tutorialWalk")) {
      this.anims.remove("tutorialWalk");
    }

    if (this.anims.exists("tutorialRun")) {
      this.anims.remove("tutorialRun");
    }

    if (this.anims.exists("tutorialDive")) {
      this.anims.remove("tutorialDive");
    }

    this.anims.create({
      key: "tutorialWalk",
      frames: [
        { key: "walk0" },
        { key: "walk1" },
        { key: "walk2" },
        { key: "walk3" },
        { key: "walk4" },
        { key: "walk5" },
        { key: "walk6" },
        { key: "walk7" },
      ],
      frameRate: 8,
      repeat: -1,
    });

    this.anims.create({
      key: "tutorialRun",
      frames: [
        { key: "run1" },
        { key: "run2" },
        { key: "run3" },
        { key: "run4" },
        { key: "run5" },
      ],
      frameRate: 10,
      repeat: -1,
    });

    this.anims.create({
      key: "tutorialDive",
      frames: [
        { key: "dive1" },
        { key: "dive2" },
        { key: "dive3" },
        { key: "dive4" },
        { key: "dive5" },
      ],
      frameRate: 10,
      repeat: -1,
    });

    this.time.delayedCall(1000, () => {
      this.openTutorialMessage({
        title:
          this.lang === "es"
            ? "¡Bienvenido a Verde Origen!"
            : "Welcome to Green Origin!",

        body: this.isMobileTutorial
          ? this.lang === "es"
            ? "¿Podemos cultivar plantas de una forma más limpia y sostenible? ¡Vamos a descubrirlo! En este juego, verás caer gotas azules de agua y gotas verdes de agua con biofertilizante. Atrápalas con tu materita para nutrir la tierra y hacer crecer tu plantita.\n\nPrimero, aprende los controles siguiendo las instrucciones en la parte superior."
            : "Can we grow plants in a cleaner, more sustainable way? Let’s find out! In this game, you’ll see blue water drops and green drops containing water and biofertilizer. Catch them with your pot to nourish the soil and grow your plant.\n\nFirst, learn the controls by following the instructions at the top."
          : this.lang === "es"
            ? "\n¿Podemos cultivar plantas de una forma más limpia y sostenible? ¡Vamos a descubrirlo! \n\n En este juego, verás caer gotas azules de agua y gotas verdes de agua con biofertilizante. Atrápalas con tu materita para nutrir la tierra y hacer crecer tu plantita.\n\nPrimero, aprende los controles siguiendo las instrucciones del panel derecho."
            : "\nCan we grow plants in a cleaner, more sustainable way? Let’s find out! \n\nIn this game, you’ll see blue water drops and green drops containing water and biofertilizer. Catch them with your pot to nourish the soil and grow your plant.\n\nFirst, learn the controls by following the instructions in the right panel.",

        buttonText: this.lang === "es" ? "¡Comenzar!" : "Start!",

        onClose: () => {
          this.setTutorialInstructions([this.tt("moveRight")]);

          this.tutorialUnlockedKeys = ["right"];
          this.setTutorialControls(this.tutorialUnlockedKeys);

          this.tutorialStep = "moveRight";
          this.tutorialStartX = this.tutorialPlayer.x;
          this.tutorialPreviousX = this.tutorialPlayer.x;
        },
      });
    });
  }

  tt(key, vars = {}) {
    const es = this.lang === "es";
    const mobile = this.isMobileTutorial;

    const controlText = (desktopEs, desktopEn, mobileEs, mobileEn) =>
      mobile ? (es ? mobileEs : mobileEn) : es ? desktopEs : desktopEn;

    const text = {
      movedRight: es ? "Movido a la derecha ✓" : "Moved right ✓",
      movedLeft: es ? "Movido a la izquierda ✓" : "Moved left ✓",
      crouched: es ? "Agachado ✓" : "Crouched ✓",
      heldPotUp: es ? "Maceta arriba ✓" : "Pot held up ✓",
      jumped: es ? "Salto completado ✓" : "Jump completed ✓",
      longJump: es ? "Salto largo ✓" : "Long jump ✓",
      ran: es ? "Corrida completada ✓" : "Run completed ✓",
      glided: es ? "Planeo completado ✓" : "Glide completed ✓",
      springDive: es ? "Caída con resorte ✓" : "Spring dive ✓",
      airPotHold: es ? "Maceta en el aire ✓" : "Pot held in air ✓",
      caughtDrops: es ? "Gotas atrapadas ✓" : "Drops caught ✓",
      vacuumUsed: es ? "Gravedad usada ✓" : "Vacuum used ✓",
      stormUsed: es ? "Tormenta usada ✓" : "Storm used ✓",
      bounceUsed: es ? "Goma usada ✓" : "Bounce used ✓",

      catchPowerStar: es
        ? `Atrapa la estrella de ${vars.powerLabel}`
        : `Catch the ${vars.powerLabel} Star`,
      catchPowerStarDone: es
        ? `Estrella de ${vars.powerLabel} atrapada ✓`
        : `${vars.powerLabel} Star caught ✓`,
      activatePower: controlText(
        `Activa el superpoder ${vars.powerLabel}. Presiona ${vars.powerKey}.`,
        `Activate the ${vars.powerLabel} upgrade. Press ${vars.powerKey}.`,
        `Activa el superpoder ${vars.powerLabel}. Presiona ${vars.powerKey}.`,
        `Activate the ${vars.powerLabel} upgrade. Press ${vars.powerKey}.`,
      ),

      greatWorkTitle: es ? "¡Muy bien!" : "Great job!",

      powerIntroBody: es
        ? "¡Ya sabes moverte y atrapar gotas! Ahora vienen los superpoderes. De vez en cuando aparecerán estrellas que esconden valiosos superpoderes. ¡Atrápalas! Cada estrella te hará una pregunta. Si aciertas, ganarás puntos extra y desbloquearás uno de tres superpoderes: Gravedad, Tormenta o Goma.\n\n¡Ve por la primera estrella!"
        : "You know how to move and catch drops. Now for upgrades! Every so often, stars will appear with valuable upgrades. Catch them! Each star will give you a question. Answer correctly to earn extra points and unlock one of three upgrades: Vacuum, Storm, or Bounce.\n\nGo get your first star!",

      starChallenge: es ? "Desafío de superpoder" : "Upgrade Challenge",

      vacuumComplete: es ? "¡Gravedad dominada!" : "Vacuum mastered!",
      stormComplete: es ? "¡Tormenta dominada!" : "Storm mastered!",
      bounceComplete: es ? "¡Goma dominada!" : "Bounce mastered!",

      tutorialCompleteTitle: es
        ? "¡Tutorial completado!"
        : "Tutorial complete!",

      tutorialCompleteBody: es
        ? "¡Ya estás listo para jugar! Guarda tus superpoderes y combina varios para atrapar aún más gotas.\n\n¡Ojo con el lodo! Si tú o una gota caen ahí, una salpicadura te tapará la vista.\n\n¡Haz crecer esa plantita!"
        : "You're ready to play! Save your upgrades and combine them to catch even more drops.\n\nWatch out for mud! If you or a drop lands in it, a splash will block your view.\n\nNow grow that plant!",
      moveRight: controlText(
        "Usa la flecha derecha para moverte hacia la derecha.",
        "Use the Right Arrow key to move right.",
        "Presiona → para moverte hacia la derecha.",
        "Press → to move right.",
      ),
      moveLeft: controlText(
        "Usa la flecha izquierda para moverte hacia la izquierda.",
        "Use the Left Arrow key to move left.",
        "Presiona ← para moverte hacia la izquierda.",
        "Press ← to move left.",
      ),
      crouch: controlText(
        "Presiona la flecha abajo para agacharte.",
        "Press the Down Arrow key to crouch.",
        "Presiona ↓ para agacharte.",
        "Press ↓ to crouch.",
      ),
      holdUp: controlText(
        "Presiona la flecha arriba para sostener la maceta sobre tu cabeza.",
        "Press the Up Arrow key to hold the plant pot above your head.",
        "Presiona ↑ para sostener la maceta sobre tu cabeza.",
        "Press ↑ to hold the plant pot above your head.",
      ),
      jump: controlText(
        "Presiona Z para saltar.",
        "Press Z to jump.",
        "Presiona SALTAR.",
        "Press JUMP.",
      ),
      horizontalJump: controlText(
        "Salta mientras te mueves a la izquierda o derecha para cubrir distancia.",
        "Jump while moving left or right to cover horizontal distance.",
        "Mantén presionado ← o → y presiona SALTAR para cubrir distancia.",
        "Hold ← or → and press JUMP to cover horizontal distance.",
      ),
      run: controlText(
        "Mantén X mientras te mueves a la izquierda o derecha para correr.",
        "Hold X while moving left or right to run.",
        "Mantén presionado CORRE mientras te mueves con ← o →.",
        "Hold RUN while moving with ← or →.",
      ),
      glide: controlText(
        "Mantén X en el aire para planear y cubrir distancia.",
        "Hold X while in the air to glide and cover distance.",
        "Mantén presionado CORRE en el aire para planear.",
        "Hold RUN while in the air to glide.",
      ),
      springInstruction: controlText(
        "Salta sobre el resorte y luego presiona abajo mientras estás en el aire.",
        "Jump onto the spring, then press Down while in the air.",
        "Salta sobre el resorte y luego presiona ↓ mientras estás en el aire.",
        "Jump onto the spring, then press ↓ while in the air.",
      ),
      airHoldInstruction: controlText(
        "Salta y presiona la flecha arriba en el aire para sostener la maceta sobre tu cabeza.",
        "Jump and press the Up Arrow key while in the air to hold the pot above your head.",
        "Presiona SALTAR y luego ↑ en el aire para sostener la maceta sobre tu cabeza.",
        "Press JUMP and then ↑ in the air to hold the pot above your head.",
      ),
      catchDrops: es
        ? `Atrapa 5 gotas. (${vars.count ?? 0}/5)`
        : `Catch 5 drops. (${vars.count ?? 0}/5)`,

      greatJob: es ? "¡Muy bien!" : "Great job!",
      nice: es ? "¡Bien!" : "Nice!",
      good: es ? "¡Correcto!" : "Good!",
      excellent: es ? "¡Excelente!" : "Excellent!",
      niceJump: es ? "¡Buen salto!" : "Nice jump!",
      greatDistance: es ? "¡Gran distancia!" : "Great distance!",
      fast: es ? "¡Rápido!" : "Fast!",
      smoothGlide: es ? "¡Buen planeo!" : "Smooth glide!",
      springDiveMsg: es ? "¡Caída con resorte!" : "Spring dive!",
      potUp: es ? "¡Maceta arriba!" : "Pot up!",
      dropsCaught: es ? "¡Gotas atrapadas!" : "Drops caught!",

      continue: es ? "Continuar" : "Continue",
      finish: es ? "Finalizar" : "Finish",
      finalAnswer: es ? "¿Respuesta final?" : "Final Answer?",
      correctPowerStored: es
        ? "¡Correcto! Poder guardado."
        : "Correct! Power stored.",
      tryAgain: es ? "No. Inténtalo otra vez." : "Nope. Try again.",
    };

    return text[key] || key;
  }

  powerLabel(powerName) {
    const es = this.lang === "es";

    if (powerName === "vacuum") return es ? "Gravedad" : "Vacuum";
    if (powerName === "storm") return es ? "Tormenta" : "Storm";
    return es ? "Goma" : "Bounce";
  }

  baseCompletedLines() {
    return [
      this.tt("movedRight"),
      this.tt("movedLeft"),
      this.tt("crouched"),
      this.tt("heldPotUp"),
      this.tt("jumped"),
      this.tt("longJump"),
      this.tt("ran"),
      this.tt("glided"),
      this.tt("springDive"),
      this.tt("airPotHold"),
      this.tt("caughtDrops"),
    ];
  }

  openTutorialMessage({
    title,
    body,
    buttonText = "Continue",
    onClose,
    panelY = 235,
    panelWidth = 560,
    panelHeight = 450,
    bodyY = 225,
    bodyFontSize = "17px",
    bodyWrapWidth = 540,
    buttonY = 400,
  }) {
    this.tutorialModalActive = true;

    const overlay = this.add
      .rectangle(400, 300, 800, 600, 0x000000, 0.62)
      .setDepth(3000)
      .setInteractive();

    const panel = this.add
      .rectangle(400, panelY, panelWidth, panelHeight, 0x18231d, 0.98)
      .setStrokeStyle(3, 0x769b55)
      .setDepth(3001);

    const titleText = this.add
      .text(400, 80, title, {
        fontFamily: '"Russo One", sans-serif',
        fontSize: "28px",
        color: "#ecfccb",
        align: "center",
      })
      .setOrigin(0.5)
      .setDepth(3002);

    const bodyText = this.add
      .text(400, bodyY, body, {
        fontSize: bodyFontSize,
        color: "#ffffff",
        align: "center",
        lineSpacing: 6,
        wordWrap: { width: bodyWrapWidth },
        fontFamily: '"Montserrat", sans-serif',
      })
      .setOrigin(0.5)
      .setDepth(3002);

    const continueButton = this.add
      .rectangle(400, buttonY, 260, 48, 0x65a30d, 1)
      .setStrokeStyle(2, 0xd9f99d)
      .setDepth(3002)
      .setInteractive({ useHandCursor: true });

    const continueText = this.add
      .text(400, buttonY, buttonText, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "18px",
        color: "#ffffff",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(3003);

    const modalItems = [
      overlay,
      panel,
      titleText,
      bodyText,
      continueButton,
      continueText,
    ];

    const closeMessage = () => {
      this.input.keyboard.off("keydown-ENTER", closeMessage);
      modalItems.forEach((item) => item.destroy());
      this.tutorialModalActive = false;
      if (onClose) onClose();
    };

    continueButton.on("pointerdown", () => {
      this.sound.play("selected", { volume: this.getEffectsVolume() * 0.5 });
      closeMessage();
    });

    this.input.keyboard.on("keydown-ENTER", closeMessage);
  }

  setTutorialInstructions(instructions) {
    const panel = document.getElementById("right-ui-panel");
    if (!panel) return;

    if (!this.fadedTutorialInstructions) {
      this.fadedTutorialInstructions = new Set();
    }

    const visibleInstructions = instructions.filter((instruction) => {
      const completed = instruction.includes("✓");

      if (!completed) {
        return true;
      }

      if (this.fadedTutorialInstructions.has(instruction)) {
        return false;
      }

      this.fadedTutorialInstructions.add(instruction);
      return true;
    });

    const instructionMarkup = visibleInstructions
      .map((instruction) => {
        const completed = instruction.includes("✓");

        return `
          <div class="rounded-md px-2 py-1.5 mb-2 text-left border ${
            completed
              ? "tutorial-instruction-fade border-[#405747] bg-[#18231d]/70"
              : "border-[#769b55] bg-[#0b1510]"
          }">
            <p class="text-[#f7faf4] text-xs leading-snug font-['Montserrat']">${instruction}</p>
          </div>
        `;
      })
      .join("");

    panel.style.visibility = "visible";

    panel.innerHTML = `
      <div class="h-full flex flex-col text-white">
        <div class="right-panel-logo-area">
          <img
            src="/assets/Ruta-N.png"
            alt="Ruta N"
            class="ruta-n-logo"
          />
        </div>

        <div class="tutorial-desktop-instructions min-h-0 flex-1 pr-1 flex flex-col">
          <div class="flex flex-col">
            ${instructionMarkup}
          </div>

          <div id="tutorial-power-hud" class="mt-auto pt-4">
            ${this.buildTutorialPowerHudMarkup()}
          </div>
        </div>
      </div>
    `;

    if (this.mobileTutorialInstructions) {
      this.mobileTutorialInstructions.innerHTML = instructionMarkup;
    }
  }

  buildTutorialPowerHudMarkup() {
    if (!this.tutorialPowerHud) return "";

    const { powerName, state, timeLeft } = this.tutorialPowerHud;
    const powerKey =
      powerName === "vacuum" ? "1" : powerName === "storm" ? "2" : "3";
    const powerLabel = this.powerLabel(powerName);
    const charged = state === "charged";
    const active = state === "active";

    const rowClass = charged
      ? "flex items-center gap-3 text-yellow-300"
      : "flex items-center gap-3 text-gray-500";

    const keyClass = charged
      ? "relative w-10 h-10 rounded-lg border border-yellow-300 bg-yellow-400 text-[#111827] flex items-center justify-center text-lg font-bold animate-pulse shadow-lg overflow-hidden"
      : "relative w-10 h-10 rounded-lg border border-gray-600 bg-gray-800 flex items-center justify-center text-lg font-bold overflow-hidden";

    const timerClass = active
      ? "absolute inset-0 bg-black/60 text-white flex items-center justify-center text-sm font-bold"
      : "hidden absolute inset-0 bg-black/60 text-white flex items-center justify-center text-sm font-bold";

    return `
      <div class="pt-4 border-t border-gray-600">
        <p class="text-sm text-gray-400 mb-3">
          ${this.lang === "es" ? "Poderes" : "Powers"}
        </p>

        <div class="${rowClass}">
          <div class="${keyClass}">
            <span>${powerKey}</span>
            <span class="${timerClass}">${active ? timeLeft : ""}</span>
          </div>

          <p class="text-sm font-semibold tracking-wide">
            ${powerLabel}
          </p>
        </div>
      </div>
    `;
  }

  refreshTutorialPowerHud() {
    const hud = document.getElementById("tutorial-power-hud");
    if (!hud) return;

    hud.innerHTML = this.buildTutorialPowerHudMarkup();
  }

  setTutorialPowerHud(powerName, state, timeLeft = null) {
    this.tutorialPowerHud = { powerName, state, timeLeft };
    this.refreshTutorialPowerHud();
  }

  startTutorialPowerHudTimer(powerName, durationSeconds = 8) {
    if (this.tutorialPowerHudTimerEvent) {
      this.tutorialPowerHudTimerEvent.remove(false);
      this.tutorialPowerHudTimerEvent = null;
    }

    let timeLeft = durationSeconds;
    this.setTutorialPowerHud(powerName, "active", timeLeft);

    this.tutorialPowerHudTimerEvent = this.time.addEvent({
      delay: 1000,
      repeat: durationSeconds - 1,
      callback: () => {
        timeLeft--;

        if (timeLeft > 0) {
          this.setTutorialPowerHud(powerName, "active", timeLeft);
          return;
        }

        this.setTutorialPowerHud(powerName, "spent");
        this.tutorialPowerHudTimerEvent = null;
      },
    });
  }

  setTutorialControls(unlockedKeys = []) {
    const mobileKeyMap = {
      1: "one",
      2: "two",
      3: "three",
    };

    const mobileUnlockedKeys = unlockedKeys.map(
      (key) => mobileKeyMap[key] ?? key,
    );

    window.dispatchEvent(
      new CustomEvent("biorev-mobile-ui", {
        detail: {
          mode: "tutorial",
          hideRightPanel: false,
          visibleControls: ["p", ...mobileUnlockedKeys],
          powerStates: {
            one: this.storedPowers?.vacuum === true,
            two: this.storedPowers?.storm === true,
            three: this.storedPowers?.bounce === true,
          },
        },
      }),
    );

    const panel = document.getElementById("left-ui-panel");
    if (!panel) return;

    const hasUp = unlockedKeys.includes("up");
    const hasLeft = unlockedKeys.includes("left");
    const hasDown = unlockedKeys.includes("down");
    const hasRight = unlockedKeys.includes("right");
    const hasZ = unlockedKeys.includes("z");
    const hasX = unlockedKeys.includes("x");

    const keyClass =
      "w-12 h-12 rounded-lg border border-gray-600 bg-gray-800 text-gray-500 flex items-center justify-center text-xl font-bold";

    panel.innerHTML = `
    <div class="h-full flex flex-col text-white">
      <div class="developer-logo-content">
        <p class="developer-logo-label">
          Desarrollado por:
        </p>

        <img
          src="/assets/CES.png"
          alt="CES University"
          class="institution-logo"
        />

        <p class="developer-logo-label">
          En el marco de:
        </p>

        <img
          src="/assets/futumed.png"
          alt="Futumed"
          class="futumed-logo"
        />
      </div>

<p class="text-sm text-gray-400 mb-5 text-center">
  ${this.lang === "es" ? "Controles" : "Controls"}
</p>
      <div class="flex flex-col items-center gap-3">
        <div class="flex justify-center">
          ${
            hasUp
              ? `<div id="ui-key-up" class="${keyClass}">↑</div>`
              : `<div class="w-12 h-12"></div>`
          }
        </div>

        <div class="flex gap-3">
          ${
            hasLeft
              ? `<div id="ui-key-left" class="${keyClass}">←</div>`
              : `<div class="w-12 h-12"></div>`
          }

          ${
            hasDown
              ? `<div id="ui-key-down" class="${keyClass}">↓</div>`
              : `<div class="w-12 h-12"></div>`
          }

          ${
            hasRight
              ? `<div id="ui-key-right" class="${keyClass}">→</div>`
              : `<div class="w-12 h-12"></div>`
          }
        </div>
              <div class="flex gap-3 mt-4">
          ${
            hasZ
              ? `<div id="ui-key-z" class="${keyClass}">Z</div>`
              : `<div class="w-12 h-12"></div>`
          }

          ${
            hasX
              ? `<div id="ui-key-x" class="${keyClass}">X</div>`
              : `<div class="w-12 h-12"></div>`
          }
        </div>
      </div>
    </div>
  `;
  }

  showTutorialCongrats(message) {
    this.sound.play("correct", { volume: this.getEffectsVolume() * 0.6 });
    const congrats = this.add
      .text(this.tutorialPlayer.x, this.tutorialPlayer.y - 60, message, {
        fontSize: "24px",
        color: "#facc15",
        fontStyle: "bold",
        stroke: "#111827",
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(50);

    this.tweens.add({
      targets: congrats,
      y: congrats.y - 24,
      alpha: 0,
      duration: 900,
      ease: "sine.out",
      onComplete: () => congrats.destroy(),
    });
  }

  updateTutorialControlUI(unlockedKeys = []) {
    const offClass =
      "w-12 h-12 rounded-lg border border-gray-600 bg-gray-800 text-gray-500 flex items-center justify-center text-xl font-bold";

    const onClass =
      "w-12 h-12 rounded-lg border border-white bg-white text-[#111827] flex items-center justify-center text-xl font-bold shadow-lg shadow-white/30";

    const updateKey = (keyName, isDown) => {
      const key = document.getElementById(`ui-key-${keyName}`);
      if (!key || !unlockedKeys.includes(keyName)) return;
      key.className = isDown ? onClass : offClass;
    };

    updateKey("up", this.cursors.up.isDown);
    updateKey("left", this.cursors.left.isDown);
    updateKey("down", this.cursors.down.isDown);
    updateKey("right", this.cursors.right.isDown);
    updateKey("z", this.jumpKey.isDown);
    updateKey("x", this.diveKey.isDown);
  }

  syncTutorialPlayerSpriteSize() {
    const player = this.tutorialPlayer;
    if (!player || !player.body) return;

    const textureKey = player.texture?.key || "";

    const targetScale =
      textureKey === "crouch"
        ? this.tutorialCrouchSpriteScale
        : this.tutorialNewSpriteScale;

    const textureChanged = this.tutorialLastTextureKey !== textureKey;

    const scaleChanged =
      Math.abs(player.scaleX - targetScale) >= 0.000001 ||
      Math.abs(player.scaleY - targetScale) >= 0.000001;

    if (!textureChanged && !scaleChanged) {
      return;
    }

    player.setScale(targetScale);

    // Establish the collision size once from walk0.
    // It must remain constant when animations or legacy sprites
    // change the texture and scale.
    if (
      this.tutorialPlayerBodyWorldWidth === undefined ||
      this.tutorialPlayerBodyWorldHeight === undefined
    ) {
      this.tutorialPlayerBodyWorldWidth = player.width * 0.6 * targetScale;

      this.tutorialPlayerBodyWorldHeight = player.height * 0.8 * targetScale;
    }

    player.body.setSize(
      this.tutorialPlayerBodyWorldWidth / targetScale,
      this.tutorialPlayerBodyWorldHeight / targetScale,
      true,
    );

    this.tutorialLastTextureKey = textureKey;
  }

  applyTutorialUnlockedMovement(player, walkSpeed, runSpeed) {
    const canMoveLeft = this.tutorialUnlockedKeys.includes("left");
    const canMoveRight = this.tutorialUnlockedKeys.includes("right");
    const canCrouch = this.tutorialUnlockedKeys.includes("down");
    const canHoldUp = this.tutorialUnlockedKeys.includes("up");
    const canJump = this.tutorialUnlockedKeys.includes("z");
    const canRun = this.tutorialUnlockedKeys.includes("x");

    player.setVelocityX(0);

    const springDiveBlock =
      this.tutorialSpring &&
      Math.abs(player.body.center.x - this.tutorialSpring.x) < 80 &&
      player.body.bottom > this.tutorialSpring.y - 40 &&
      player.body.top < this.tutorialSpring.y + 120;

    const springPoundBlocked =
      springDiveBlock || this.time.now < this.springNoPoundUntil;

    const applyAirSprite = () => {
      if (canRun && this.diveKey.isDown) {
        player.anims.play("tutorialDive", true);
        return;
      }

      player.anims.stop();

      if (this.cursors.up.isDown) {
        player.setTexture("jumpup");
      } else {
        // New jump sprites: jump1 while rising, jump2 while falling.
        player.setTexture(player.body.velocity.y < 0 ? "jump1" : "jump2");
      }
    };

    if (canCrouch && this.cursors.down.isDown && player.body.blocked.down) {
      player.anims.stop();
      player.setTexture("crouchFinal");
      return;
    }

    if (
      canCrouch &&
      this.cursors.down.isDown &&
      !player.body.blocked.down &&
      !this.springLock &&
      !this.bounceLock &&
      !springPoundBlocked
    ) {
      player.anims.stop();

      player.setTexture("pound1");

      player.setVelocityY(400);
      return;
    }

    if (canHoldUp && this.cursors.up.isDown && player.body.blocked.down) {
      player.anims.stop();
      player.setTexture("up");
      return;
    }

    if (canJump && this.jumpKey.isDown && player.body.blocked.down) {
      player.setVelocityY(-300);
      this.isJumping = true;
      this.tutorialJumpStarted = true;
      this.tutorialHorizontalJumpStarted =
        this.tutorialStep === "horizontalJump" &&
        (this.cursors.left.isDown || this.cursors.right.isDown);
      this.tutorialPreviousX = player.x;
    }

    const currentSpeed = canRun && this.diveKey.isDown ? runSpeed : walkSpeed;

    if (canMoveLeft && this.cursors.left.isDown) {
      player.setVelocityX(-currentSpeed);
      player.setFlipX(false);

      if (!player.body.blocked.down) {
        applyAirSprite();
      } else if (canRun && this.diveKey.isDown) {
        player.anims.play("tutorialRun", true);
      } else {
        player.anims.play("tutorialWalk", true);
      }

      return;
    }

    if (canMoveRight && this.cursors.right.isDown) {
      player.setVelocityX(currentSpeed);
      player.setFlipX(true);

      if (!player.body.blocked.down) {
        applyAirSprite();
      } else if (canRun && this.diveKey.isDown) {
        player.anims.play("tutorialRun", true);
      } else {
        player.anims.play("tutorialWalk", true);
      }

      return;
    }

    if (!player.body.blocked.down) {
      applyAirSprite();
    } else {
      player.anims.stop();
      player.setTexture("walk0");
    }
  }

  spawnTutorialSpring() {
    if (this.tutorialSpring) return;

    this.tutorialSpring = this.physics.add.staticImage(120, 490, "spring1");

    this.tutorialSpring.setScale(0.1).setDepth(5).refreshBody();

    /*
     * The trigger must extend above the platform.
     * The platform begins at y = 490, so a trigger beginning there
     * cannot overlap the player before the platform stops them.
     */
    this.tutorialSpringZone = this.add.rectangle(120, 475, 40, 30, 0xff0000, 0);

    this.physics.add.existing(this.tutorialSpringZone, true);
    this.tutorialSpringZone.body.setSize(40, 30);
    this.tutorialSpringZone.setData("spring", this.tutorialSpring);

    this.physics.add.overlap(
      this.tutorialPlayer,
      this.tutorialSpringZone,
      this.hitTutorialSpring,
      null,
      this,
    );
  }

  hitTutorialSpring(player, zone) {
    if (this.springActive) return;

    // Never reactivate the spring while the player is rising.
    if (player.body.velocity.y < 0) return;

    // The player's center must be directly over the spring.
    // This prevents the edge of the player body from activating it.
    const horizontalDistance = Math.abs(player.body.center.x - zone.x);

    if (horizontalDistance > 20) return;

    const springTop = zone.body.top;
    const playerBottom = player.body.bottom;
    const previousBottom = player.body.prev.y + player.body.height;

    /*
     * Only launch when approaching the spring from above.
     * The small tolerance prevents fast downward movement from
     * skipping the exact crossing point.
     */
    const approachingFromAbove =
      previousBottom <= springTop + 8 && playerBottom >= springTop - 8;

    if (!approachingFromAbove) return;

    this.springActive = true;
    this.tutorialSpringLaunched = true;
    this.tutorialSpringDiveDistance = 0;
    this.tutorialSpringPreviousY = player.y;

    this.sound.play("springSound", {
      volume: this.getEffectsVolume() * 0.7,
    });

    const spring = zone.getData("spring");

    player.setVelocityY(-450);

    this.isDiving = false;
    this.isPounding = false;
    this.springLock = true;
    this.springNoPoundUntil = this.time.now + 150;

    player.anims.stop();
    player.setTexture("walk0");

    this.time.delayedCall(500, () => {
      this.springLock = false;
      this.bounceLock = false;
    });

    spring.setTexture("spring2");

    this.time.delayedCall(50, () => {
      if (spring?.active) {
        spring.setTexture("spring3");
      }
    });

    this.time.delayedCall(100, () => {
      if (spring?.active) {
        spring.setTexture("spring1");
      }

      this.springActive = false;
    });
  }

  updateTutorialPlantHitbox() {
    const player = this.tutorialPlayer;
    if (!player || !player.body || !this.plant) return;

    let offsetX = player.flipX ? 28 : -28;
    let offsetY = 0;

    if (player.texture.key.startsWith("dive")) {
      offsetX = player.flipX ? 52 : -52;
    }

    if (player.texture.key === "pound1") {
      offsetX = 0;
      offsetY = -player.body.height * 0.6;
    }

    if (player.texture.key === "jumpup") {
      offsetX = player.flipX ? 6 : 2;
      offsetY = -player.body.height * 0.6;
    }

    if (player.texture.key === "crouch") {
      offsetX = 2;
      offsetY = -player.body.height * 0.6;
    }

    if (player.texture.key === "up") {
      offsetX = 2;
      offsetY = -player.body.height * 0.65;
    }

    this.plant.setPosition(
      player.body.center.x + offsetX,
      player.body.center.y + offsetY,
    );

    if (this.plantHitboxDebug) {
      this.plantHitboxDebug.setPosition(this.plant.x, this.plant.y);
    }
  }

  spawnTutorialDrops(forceRestart = false) {
    if (forceRestart && this.tutorialDropSpawnEvent) {
      this.tutorialDropSpawnEvent.remove(false);
      this.tutorialDropSpawnEvent = null;
    }

    if (this.tutorialDrops && this.tutorialDropSpawnEvent) return;

    if (!this.tutorialDrops) {
      this.tutorialDrops = this.physics.add.group();
    }
    this.tutorialDropSpawnEvent = this.time.addEvent({
      delay: 900,
      loop: true,
      callback: () => {
        if (
          this.tutorialStep !== "catchDrops" &&
          this.tutorialStep !== "catchStar" &&
          this.tutorialStep !== "activatePower" &&
          this.tutorialStep !== "powerQuestion"
        )
          return;
        const x = Phaser.Math.Between(50, 750);
        const isGreen = Math.random() < 0.5;
        const key = isGreen ? "greendrop" : "bluedrop";
        const color = isGreen ? 0x66ff66 : 0x66ccff;

        this.createTutorialDropWarning(x, color);

        this.time.delayedCall(450, () => {
          if (
            this.tutorialStep !== "catchDrops" &&
            this.tutorialStep !== "catchStar" &&
            this.tutorialStep !== "activatePower" &&
            this.tutorialStep !== "powerQuestion"
          )
            return;

          const drop = this.physics.add.image(x, 0, key);
          drop.setBounce(this.bounceActive ? 1 : 0.2);
          drop.setCollideWorldBounds(true);
          drop.body.setGravityY(200);
          drop.setDamping(true);
          drop.setDrag(0.95);
          drop.setMaxVelocity(100);
          drop.setScale(0.05);
          drop.setData("type", isGreen ? "green" : "blue");
          drop.setData("caught", false);

          this.tutorialDrops.add(drop);
        });
      },
    });

    if (!this.tutorialDropOverlap) {
      this.tutorialDropOverlap = this.physics.add.overlap(
        this.plant,
        this.tutorialDrops,
        this.catchTutorialDrop,
        null,
        this,
      );
    }

    if (!this.tutorialDropCollider) {
      this.tutorialDropCollider = this.physics.add.collider(
        this.tutorialDrops,
        this.platforms,
        this.tutorialDropHitsPlatform,
        null,
        this,
      );
    }
  }

  catchTutorialDrop(player, drop) {
    if (!drop.active || drop.getData("caught")) return;

    drop.setData("caught", true);

    const type = drop.getData("type");

    if (type === "green") {
      this.sound.play("greenSound", { volume: this.getEffectsVolume() * 0.6 });
    } else {
      this.sound.play("blueSound", { volume: this.getEffectsVolume() * 0.6 });
    }

    drop.destroy();

    if (this.tutorialStep !== "catchDrops") {
      return;
    }

    this.tutorialDropsCaught++;

    this.setTutorialInstructions([
      this.tt("movedRight"),
      this.tt("movedLeft"),
      this.tt("crouched"),
      this.tt("heldPotUp"),
      this.tt("jumped"),
      this.tt("longJump"),
      this.tt("ran"),
      this.tt("glided"),
      this.tt("springDive"),
      this.tt("airPotHold"),
      this.tt("catchDrops", {
        count: this.tutorialDropsCaught,
      }),
    ]);

    if (this.tutorialDropsCaught >= 5) {
      if (this.tutorialDropSpawnEvent) {
        this.tutorialDropSpawnEvent.remove(false);
        this.tutorialDropSpawnEvent = null;
      }

      this.showTutorialCongrats(this.tt("dropsCaught"));

      this.setTutorialInstructions([
        this.tt("movedRight"),
        this.tt("movedLeft"),
        this.tt("crouched"),
        this.tt("heldPotUp"),
        this.tt("jumped"),
        this.tt("longJump"),
        this.tt("ran"),
        this.tt("glided"),
        this.tt("springDive"),
        this.tt("airPotHold"),
        this.tt("caughtDrops"),
      ]);

      this.tutorialStep = "powerIntro";
      player.setVelocity(0, 0);

      this.time.delayedCall(1000, () => {
        this.openTutorialMessage({
          title: this.tt("greatWorkTitle"),
          body: this.tt("powerIntroBody"),
          buttonText: this.tt("continue"),
          onClose: () => {
            this.setTutorialInstructions([
              this.tt("catchPowerStar", {
                powerLabel: this.powerLabel("vacuum"),
              }),
            ]);

            this.tutorialStep = "catchStar";
            this.tutorialDropsCaught = 0;
            this.spawnTutorialDrops(true);
            this.spawnTutorialStar("vacuum");
          },
        });
      });
    }
  }

  createTutorialDropWarning(x, color) {
    const warning = this.add
      .triangle(x, 18, 0, 18, 12, 0, 24, 18, color, 0.75)
      .setDepth(1001)
      .setOrigin(0.5);

    this.tweens.add({
      targets: warning,
      alpha: 0.15,
      yoyo: true,
      repeat: 3,
      duration: 90,
      ease: "sine.inOut",
      onComplete: () => warning.destroy(),
    });
  }

  tutorialDropHitsPlatform(drop, platform) {
    if (!drop.active) return;

    const x = drop.x;
    const y = drop.y;

    if (this.bounceActive) {
      const now = this.time.now;
      const lastBounceFx = drop.getData("lastBounceFx") || 0;
      const bounceFxCount = drop.getData("bounceFxCount") || 0;

      if (bounceFxCount < 5 && now - lastBounceFx > 80) {
        const type = drop.getData("type");
        const color = type === "green" ? 0x66ff66 : 0x66ccff;

        this.createTutorialDropBounceWarp(x, y, color);
        drop.setData("lastBounceFx", now);
        drop.setData("bounceFxCount", bounceFxCount + 1);
      }

      return;
    }

    const fragments = 4;

    for (let i = 0; i < fragments; i++) {
      const piece = this.add
        .image(x, y, drop.texture.key)
        .setScale(0.03)
        .setDepth(50);

      const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);

      this.tweens.add({
        targets: piece,
        x: x + Math.cos(angle) * 40,
        y: y + Math.sin(angle) * 40,
        alpha: 0,
        duration: 300,
        ease: "quad.out",
        onComplete: () => piece.destroy(),
      });
    }

    this.sound.play("mudSplash2", { volume: this.getEffectsVolume() * 0.2 });

    drop.destroy();
  }

  createTutorialBounceWarp(x, y) {
    this.cameras.main.shake(80, 0.004);

    const warp = this.add
      .ellipse(x, y - 25, 30, 10, 0xffffff, 0.22)
      .setDepth(998)
      .setScale(1)
      .setAlpha(0.7);

    const ring = this.add
      .ellipse(x, y - 25, 40, 12)
      .setStrokeStyle(3, 0xffffff, 0.55)
      .setDepth(998)
      .setScale(1)
      .setAlpha(0.8);

    this.tweens.add({
      targets: warp,
      scaleX: 3.2,
      scaleY: 0.45,
      alpha: 0,
      duration: 220,
      ease: "sine.out",
      onComplete: () => warp.destroy(),
    });

    this.tweens.add({
      targets: ring,
      scaleX: 3.8,
      scaleY: 0.6,
      alpha: 0,
      duration: 260,
      ease: "sine.out",
      onComplete: () => ring.destroy(),
    });
  }

  createTutorialDropBounceWarp(x, y, color) {
    const warp = this.add
      .ellipse(x, y - 1, 14, 5, color, 0.22)
      .setDepth(998)
      .setScale(1)
      .setAlpha(0.65);

    const ring = this.add
      .ellipse(x, y - 1, 18, 6)
      .setStrokeStyle(2, color, 0.55)
      .setDepth(998)
      .setScale(1)
      .setAlpha(0.75);

    this.tweens.add({
      targets: warp,
      scaleX: 2.2,
      scaleY: 0.45,
      alpha: 0,
      duration: 160,
      ease: "sine.out",
      onComplete: () => warp.destroy(),
    });

    this.tweens.add({
      targets: ring,
      scaleX: 2.6,
      scaleY: 0.55,
      alpha: 0,
      duration: 190,
      ease: "sine.out",
      onComplete: () => ring.destroy(),
    });
  }

  spawnTutorialStar(powerName = "vacuum") {
    if (this.tutorialStar) {
      this.tutorialStar.destroy();
    }

    this.tutorialCurrentPower = powerName;

    this.tutorialStar = this.physics.add.image(
      Phaser.Math.Between(140, 660),
      Phaser.Math.Between(330, 470),
      "star",
    );

    this.tutorialStar.setScale(0.08);
    this.tutorialStar.setData("powerName", powerName);

    this.tutorialStar.body.setAllowGravity(false);
    this.tutorialStar.body.setImmovable(true);
    this.tutorialStar.body.setVelocity(0, 0);
    this.tutorialStar.body.setCollideWorldBounds(false);
    this.tutorialStar.body.moves = false;

    this.tutorialStar.body.setSize(100, 100);
    this.tutorialStar.body.setOffset(
      (this.tutorialStar.width - 100) / 2,
      (this.tutorialStar.height - 100) / 2,
    );

    if (this.tutorialStarHitboxDebug) {
      this.tutorialStarHitboxDebug.destroy();
    }

    this.tutorialStarHitboxDebug = this.add
      .rectangle(0, 0, 100, 100, 0xff0000, 0)
      .setStrokeStyle(0, 0xff0000, 0)
      .setVisible(false)
      .setDepth(2000);

    const wander = () => {
      if (!this.tutorialStar || !this.tutorialStar.active) return;

      this.tweens.add({
        targets: this.tutorialStar,
        x: Phaser.Math.Between(120, 680),
        y: Phaser.Math.Between(310, 470),
        duration: Phaser.Math.Between(1400, 2400),
        ease: "sine.inOut",
        onComplete: wander,
      });
    };

    wander();

    // Star collection is handled by checkTutorialStarCatch()
    // so there is only one consistent collision test.
  }

  collectTutorialStar(plant, star) {
    if (this.tutorialStep !== "catchStar") return;
    if (!star || !star.active) return;

    const powerName = star.getData("powerName") || this.tutorialCurrentPower;

    star.destroy();
    this.tutorialStar = null;
    this.sound.play("powerGrab", { volume: this.getEffectsVolume() * 0.5 });

    this.tutorialStep = "powerQuestion";
    this.openTutorialPowerQuestion(powerName);
  }

  openTutorialPowerQuestion(powerName = "vacuum") {
    const overlay = this.add
      .rectangle(400, 300, 800, 600, 0x000000, 0.72)
      .setDepth(3000)
      .setInteractive();

    const panel = this.add
      .rectangle(400, 300, 620, 420, 0x18231d, 0.98)
      .setStrokeStyle(3, 0x769b55)
      .setDepth(3001);

    const title = this.add
      .text(400, 125, this.tt("starChallenge"), {
        fontFamily: '"Russo One", sans-serif',
        fontSize: "28px",
        color: "#ecfccb",
      })
      .setOrigin(0.5)
      .setDepth(3002);

    const tutorialQuestions =
      this.lang === "es"
        ? [
            {
              question: "¿Qué necesitan las plantas para crecer?",
              answers: [
                {
                  text: "Agua, luz y nutrientes.",
                  correct: true,
                },
                {
                  text: "Café y ocho horas de sueño.",
                  correct: false,
                },
                {
                  text: "Inteligencia artificial.",
                  correct: false,
                },
                {
                  text: "Un buen caldo de costilla los domingos.",
                  correct: false,
                },
              ],
            },
            {
              question: "¿Para qué sirve un fertilizante?",
              answers: [
                {
                  text: "Para que aprendan fotosíntesis más rápido.",
                  correct: false,
                },
                {
                  text: "Para enseñarles a caminar.",
                  correct: false,
                },
                {
                  text: "Para convencerlas de que le echen ganas.",
                  correct: false,
                },
                {
                  text: "Para aportar nutrientes a las plantas.",
                  correct: true,
                },
              ],
            },
            {
              question: "¿Qué parte de la planta absorbe agua?",
              answers: [
                {
                  text: "Las flores.",
                  correct: false,
                },
                {
                  text: "El Bluetooth.",
                  correct: false,
                },
                {
                  text: "Las raíces.",
                  correct: true,
                },
                {
                  text: "La boca.",
                  correct: false,
                },
              ],
            },
          ]
        : [
            {
              question: "What do plants need to grow?",
              answers: [
                {
                  text: "Water, light, and nutrients.",
                  correct: true,
                },
                {
                  text: "Coffee and eight hours of sleep.",
                  correct: false,
                },
                {
                  text: "Artificial intelligence.",
                  correct: false,
                },
                {
                  text: "A good bowl of rib soup on Sundays.",
                  correct: false,
                },
              ],
            },
            {
              question: "What is a fertilizer used for?",
              answers: [
                {
                  text: "To help plants learn photosynthesis faster.",
                  correct: false,
                },
                {
                  text: "To teach them how to walk.",
                  correct: false,
                },
                {
                  text: "To convince them to try harder.",
                  correct: false,
                },
                {
                  text: "To provide nutrients to plants.",
                  correct: true,
                },
              ],
            },
            {
              question: "What part of a plant absorbs water?",
              answers: [
                {
                  text: "The flowers.",
                  correct: false,
                },
                {
                  text: "The Bluetooth.",
                  correct: false,
                },
                {
                  text: "The roots.",
                  correct: true,
                },
                {
                  text: "The mouth.",
                  correct: false,
                },
              ],
            },
          ];

    const questionData =
      powerName === "vacuum"
        ? tutorialQuestions[0]
        : powerName === "storm"
          ? tutorialQuestions[1]
          : tutorialQuestions[2];

    const question = this.add
      .text(400, 180, questionData.question, {
        fontSize: "18px",
        fontFamily: '"Montserrat", sans-serif',
        color: "#ffffff",
        align: "center",
        wordWrap: { width: 520 },
      })
      .setOrigin(0.5)
      .setDepth(3002);

    const feedback = this.add
      .text(400, 470, "", {
        fontSize: "19px",
        color: "#ffffff",
        fontStyle: "bold",
        fontFamily: '"Montserrat", sans-serif',
      })
      .setOrigin(0.5)
      .setDepth(3002);

    const finalButton = this.add
      .rectangle(400, 525, 220, 42, 0x4b5563, 1)
      .setDepth(3002);

    const finalText = this.add
      .text(400, 525, "Final Answer?", {
        fontSize: "18px",
        color: "#9ca3af",
        fontStyle: "bold",
        fontFamily: '"Montserrat", sans-serif',
      })
      .setOrigin(0.5)
      .setDepth(3003);

    const answers = Phaser.Utils.Array.Shuffle([...questionData.answers]);

    let selectedIndex = null;
    let answerSubmitted = false;
    const answerButtons = [];

    const modalItems = [
      overlay,
      panel,
      title,
      question,
      feedback,
      finalButton,
      finalText,
    ];

    const closeModal = () => {
      this.input.keyboard.off("keydown-ONE", selectOne);
      this.input.keyboard.off("keydown-TWO", selectTwo);
      this.input.keyboard.off("keydown-THREE", selectThree);
      this.input.keyboard.off("keydown-FOUR", selectFour);
      this.input.keyboard.off("keydown-ENTER", pressReturn);

      modalItems.forEach((item) => item.destroy());

      this.storedPowers[powerName] = true;
      this.setTutorialPowerHud(powerName, "charged");

      const powerLabel = this.powerLabel(powerName);

      const powerKey =
        powerName === "vacuum" ? "1" : powerName === "storm" ? "2" : "3";

      const completedLines = [];

      if (powerName === "storm" || powerName === "bounce") {
        completedLines.push(this.tt("vacuumUsed"));
      }

      if (powerName === "bounce") {
        completedLines.push(this.tt("stormUsed"));
      }

      this.setTutorialInstructions([
        ...completedLines,
        this.tt("catchPowerStarDone", { powerLabel }),
        this.tt("activatePower", { powerLabel, powerKey }),
      ]);

      this.setTutorialControls([...this.tutorialUnlockedKeys, powerKey]);

      this.tutorialStep = "activatePower";
    };

    answers.forEach((answer, index) => {
      const y = 260 + index * 48;

      const button = this.add
        .rectangle(400, y, 520, 38, 0x0b1510, 1)
        .setStrokeStyle(1, 0x405747)
        .setDepth(3002)
        .setInteractive({ useHandCursor: true });

      const numberText = this.add
        .text(165, y, `${index + 1}`, {
          fontSize: "18px",
          color: "#ffffff",
          fontStyle: "bold",
          fontFamily: '"Montserrat", sans-serif',
        })
        .setOrigin(0.5)
        .setDepth(3003);

      const answerText = this.add
        .text(420, y, answer.text, {
          fontSize: "15px",
          color: "#ffffff",
          fontFamily: '"Montserrat", sans-serif',
          align: "left",
          wordWrap: { width: 440 },
        })
        .setOrigin(0.5)
        .setDepth(3003);

      modalItems.push(button, numberText, answerText);

      const selectAnswer = () => {
        this.sound.play("selected", { volume: this.getEffectsVolume() * 0.5 });

        selectedIndex = index;

        answerButtons.forEach(
          ({ button: otherButton, numberText, answerText }, i) => {
            if (i === selectedIndex) {
              otherButton.setFillStyle(0x374151, 1);
              otherButton.setStrokeStyle(2, 0xfacc15);
              numberText.setColor("#111827");
              answerText.setColor("#facc15");
            } else {
              otherButton.setFillStyle(0x1f2937, 1);
              otherButton.setStrokeStyle(1, 0x64748b);
              numberText.setColor("#ffffff");
              answerText.setColor("#ffffff");
            }
          },
        );

        finalButton.setFillStyle(0xfacc15, 1);
        finalButton.setInteractive({ useHandCursor: true });
        finalText.setColor("#111827");
      };

      button.on("pointerdown", selectAnswer);

      answerButtons.push({ button, numberText, answerText, answer });
    });

    const submitAnswer = () => {
      if (answerSubmitted) {
        closeModal();
        return;
      }

      if (selectedIndex === null) return;
      const selectedAnswer = answers[selectedIndex];

      this.sound.play("selected", { volume: this.getEffectsVolume() * 0.5 });

      if (!selectedAnswer.correct) {
        this.sound.play("incorrect", { volume: this.getEffectsVolume() * 0.6 });
        feedback.setText(this.tt("tryAgain"));
        feedback.setColor("#fca5a5");
        selectedIndex = null;

        finalButton.setFillStyle(0x4b5563, 1);
        finalButton.disableInteractive();
        finalText.setColor("#9ca3af");

        return;
      }

      this.sound.play("correct", { volume: this.getEffectsVolume() * 0.6 });
      feedback.setText(this.tt("correctPowerStored"));
      feedback.setColor("#86efac");
      answerSubmitted = true;

      finalText.setText(this.tt("continue"));
      finalButton.setFillStyle(0xfacc15, 1);
      finalText.setColor("#111827");
      finalButton.setInteractive({ useHandCursor: true });
    };

    const selectOne = () => {
      selectedIndex = 0;
      answerButtons[0]?.button.emit("pointerdown");
    };

    const selectTwo = () => {
      selectedIndex = 1;
      answerButtons[1]?.button.emit("pointerdown");
    };

    const selectThree = () => {
      selectedIndex = 2;
      answerButtons[2]?.button.emit("pointerdown");
    };

    const selectFour = () => {
      selectedIndex = 3;
      answerButtons[3]?.button.emit("pointerdown");
    };

    const pressReturn = () => {
      submitAnswer();
    };

    this.input.keyboard.on("keydown-ONE", selectOne);
    this.input.keyboard.on("keydown-TWO", selectTwo);
    this.input.keyboard.on("keydown-THREE", selectThree);
    this.input.keyboard.on("keydown-FOUR", selectFour);
    this.input.keyboard.on("keydown-ENTER", pressReturn);

    finalButton.on("pointerdown", submitAnswer);
  }

  activateTutorialPower(powerName = this.tutorialCurrentPower) {
    if (!this.storedPowers[powerName]) return;
    if (powerName === "vacuum" && this.vacuumActive) return;
    if (powerName === "storm" && this.tripleRainActive) return;
    if (powerName === "bounce" && this.bounceActive) return;

    this.sound.play("apply", { volume: this.getEffectsVolume() * 0.5 });

    this.storedPowers[powerName] = false;

    this.setTutorialControls(this.tutorialUnlockedKeys);
    this.startTutorialPowerHudTimer(powerName, 8);

    if (powerName === "vacuum") {
      this.vacuumActive = true;

      if (this.vacuumLoop) {
        this.vacuumLoop.stop();
      }

      this.vacuumLoop = this.sound.add("vacuumLoop", {
        loop: true,
        volume: this.getEffectsVolume() * 0.3,
      });

      this.vacuumLoop.play();
    } else if (powerName === "storm") {
      this.tripleRainActive = true;

      if (this.tutorialDropSpawnEvent) {
        this.tutorialDropSpawnEvent.delay = 200;
      }

      this.sound.play("thunderclap", { volume: this.getEffectsVolume() * 0.6 });

      this.tweens.add({
        targets: this.stormOverlay,
        alpha: 0.4,
        duration: 200,
      });
    } else if (powerName === "bounce") {
      this.bounceActive = true;

      if (this.tutorialDropSpawnEvent) {
        this.tutorialDropSpawnEvent.delay = 900;
      }

      this.tutorialDrops?.children.iterate((drop) => {
        if (!drop || !drop.active) return;
        drop.setBounce(0.8);
      });
    }
    this.time.delayedCall(8000, () => {
      if (powerName === "vacuum") {
        this.vacuumActive = false;

        if (this.vacuumLoop) {
          this.vacuumLoop.stop();
        }

        this.showTutorialCongrats(this.tt("vacuumComplete"));
        this.startNextTutorialPower("storm");
      } else if (powerName === "storm") {
        this.tripleRainActive = false;

        if (this.tutorialDropSpawnEvent) {
          this.tutorialDropSpawnEvent.delay = 900;
        }

        this.tweens.add({
          targets: this.stormOverlay,
          alpha: 0,
          duration: 300,
        });

        this.showTutorialCongrats(this.tt("stormComplete"));
        this.startNextTutorialPower("bounce");
      } else if (powerName === "bounce") {
        this.bounceActive = false;

        this.showTutorialCongrats(this.tt("bounceComplete"));
        this.setTutorialInstructions([
          this.tt("vacuumUsed"),
          this.tt("stormUsed"),
          this.tt("bounceUsed"),
        ]);

        this.tutorialStep = "powersComplete";

        this.time.delayedCall(2000, () => {
          this.openTutorialMessage({
            title: this.tt("tutorialCompleteTitle"),
            body: this.tt("tutorialCompleteBody"),
            buttonText: this.tt("finish"),
            onClose: () => {
              if (this.tutorialMusic) {
                this.tutorialMusic.stop();
              }

              window.dispatchEvent(
                new CustomEvent("biorev-mobile-ui", {
                  detail: {
                    mode: "hidden",
                    hideRightPanel: true,
                    visibleControls: [],
                    powerStates: {
                      one: false,
                      two: false,
                      three: false,
                    },
                  },
                }),
              );

              window.dispatchEvent(new Event("reset-right-panel"));

              requestAnimationFrame(() => {
                const rightPanel = document.getElementById("right-ui-panel");
                if (rightPanel) {
                  rightPanel.style.visibility = "visible";
                }

                const gameplayContent = document.getElementById(
                  "right-panel-gameplay-content",
                );
                if (gameplayContent) {
                  gameplayContent.style.display = "none";
                }
              });

              this.input.keyboard.clearCaptures();
              this.input.keyboard.resetKeys();

              this.scene.start("TitleScene");
            },
          });
        });
      }
    });
  }

  startNextTutorialPower(powerName) {
    const powerLabel = this.powerLabel(powerName);

    const completedLines = [];

    if (powerName === "storm" || powerName === "bounce") {
      completedLines.push(this.tt("vacuumUsed"));
    }

    if (powerName === "bounce") {
      completedLines.push(this.tt("stormUsed"));
    }

    completedLines.push(this.tt("catchPowerStar", { powerLabel }));
    this.setTutorialInstructions(completedLines);

    this.tutorialCurrentPower = powerName;
    this.tutorialStep = "catchStar";

    this.spawnTutorialDrops();
    this.spawnTutorialStar(powerName);
  }

  updateTutorialVacuumPull() {
    if (!this.tutorialDrops) return;

    this.vacuumParticles.setPosition(this.plant.x, this.plant.y);

    if (this.vacuumActive) {
      this.vacuumParticles.start();
    } else {
      this.vacuumParticles.stop();
    }

    this.tutorialDrops.children.iterate((drop) => {
      if (!drop || !drop.active) return;

      drop.setBounce(this.bounceActive ? 0.8 : 0.2);

      if (!this.vacuumActive) {
        drop.setAcceleration(0, 0);
        return;
      }

      const dx = this.plant.x - drop.x;
      const dy = this.plant.y - drop.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < this.vacuumRadius) {
        if (dist < 40) {
          drop.setVelocity(0, 0);
          drop.setPosition(this.plant.x, this.plant.y);
          return;
        }

        const nx = dx / dist;
        const ny = dy / dist;
        const t = 1 - dist / this.vacuumRadius;
        const strength = this.vacuumStrength * (t * t);

        drop.setAcceleration(nx * strength, ny * strength);

        const vx = drop.body.velocity.x;
        const vy = drop.body.velocity.y;
        const funnel = 0.02 + 0.15 * Math.pow(1 - dist / this.vacuumRadius, 2);

        drop.setVelocity(
          vx + (nx * 200 - vx) * funnel,
          vy + (ny * 200 - vy) * funnel,
        );
      } else {
        drop.setAcceleration(0, 0);
      }
    });
  }

  checkTutorialStarCatch() {
    if (this.tutorialStep !== "catchStar") return;
    if (!this.tutorialStar || !this.tutorialStar.active) return;
    if (!this.plant || !this.plant.body) return;

    // Only the central 40% of the visible star is collectible.
    const starHitboxScale = 0.4;
    const starHitboxWidth = this.tutorialStar.displayWidth * starHitboxScale;
    const starHitboxHeight = this.tutorialStar.displayHeight * starHitboxScale;

    const starBounds = new Phaser.Geom.Rectangle(
      this.tutorialStar.x - starHitboxWidth / 2,
      this.tutorialStar.y - starHitboxHeight / 2,
      starHitboxWidth,
      starHitboxHeight,
    );

    const plantBounds = new Phaser.Geom.Rectangle(
      this.plant.body.x,
      this.plant.body.y,
      this.plant.body.width,
      this.plant.body.height,
    );

    const plantHitsStar = Phaser.Geom.Intersects.RectangleToRectangle(
      plantBounds,
      starBounds,
    );

    if (plantHitsStar) {
      this.collectTutorialStar(this.plant, this.tutorialStar);
    }
  }

  toggleManualPause() {
    this.manualPaused = !this.manualPaused;

    if (this.manualPaused) {
      this.physics.pause();
      this.tweens.pauseAll();
      this.time.paused = true;
      this.sound.pauseAll();

      this.pauseOverlay.setVisible(true);
      this.pauseText.setVisible(true);
      this.pauseHint.setVisible(true);
    } else {
      this.time.paused = false;
      this.tweens.resumeAll();
      this.physics.resume();
      this.sound.resumeAll();

      this.pauseOverlay.setVisible(false);
      this.pauseText.setVisible(false);
      this.pauseHint.setVisible(false);
    }
  }

  update() {
    if (this.manualPaused) return;

    const player = this.tutorialPlayer;
    if (!player || !player.body) return;

    const mobileJumpReleased = this.consumeMobileRelease("z");

    const onGround = player.body.blocked.down;
    const walkSpeed = 200;
    const runSpeed = 375;
    this.plant.setPosition(player.body.center.x, player.body.center.y);

    if (
      this.tutorialStar &&
      this.tutorialStar.active &&
      this.tutorialStarHitboxDebug
    ) {
      this.tutorialStarHitboxDebug.setPosition(
        this.tutorialStar.body.center.x,
        this.tutorialStar.body.center.y,
      );
    }
    if (
      this.tutorialStep === "powerIntro" ||
      this.tutorialStep === "powerQuestion"
    ) {
      player.setVelocityX(0);
      this.updateTutorialPlantHitbox();
      this.checkTutorialStarCatch();
      return;
    }
    if (
      this.tutorialStep === "catchDrops" ||
      this.tutorialStep === "catchStar" ||
      this.tutorialStep === "activatePower" ||
      this.tutorialStep === "dropsComplete" ||
      this.tutorialStep === "powersComplete"
    ) {
      this.updateTutorialControlUI(this.tutorialUnlockedKeys);
      this.applyTutorialUnlockedMovement(player, walkSpeed, runSpeed);
      this.updateTutorialPlantHitbox();
      this.checkTutorialStarCatch();

      const wasFalling = this.wasFalling || false;
      const isFalling = player.body.velocity.y > 0;
      const justLanded = onGround && !this.wasOnGround && wasFalling;

      if (this.bounceActive && justLanded && !this.bounceLock) {
        this.createTutorialBounceWarp(player.x, player.body.bottom);

        player.setVelocityY(-350);
        this.sound.play("bounceClip", { volume: this.getEffectsVolume() * 5 });
        this.bounceLock = true;
        this.isPounding = false;
        this.isDiving = false;

        this.time.delayedCall(200, () => {
          this.bounceLock = false;
        });
      }

      this.wasOnGround = onGround;
      this.wasFalling = isFalling;

      if (this.tutorialStep === "activatePower") {
        if (
          this.tutorialCurrentPower === "vacuum" &&
          (Phaser.Input.Keyboard.JustDown(this.power1Key) ||
            this.consumeMobilePress("one"))
        ) {
          this.activateTutorialPower("vacuum");
        }

        if (
          this.tutorialCurrentPower === "storm" &&
          (Phaser.Input.Keyboard.JustDown(this.power2Key) ||
            this.consumeMobilePress("two"))
        ) {
          this.activateTutorialPower("storm");
        }

        if (
          this.tutorialCurrentPower === "bounce" &&
          (Phaser.Input.Keyboard.JustDown(this.power3Key) ||
            this.consumeMobilePress("three"))
        ) {
          this.activateTutorialPower("bounce");
        }
      }

      this.updateTutorialVacuumPull();
      return;
    }
    if (
      this.isJumping &&
      (Phaser.Input.Keyboard.JustUp(this.jumpKey) || mobileJumpReleased)
    ) {
      if (player.body.velocity.y < 0) {
        player.setVelocityY(player.body.velocity.y * 0.5);
      }

      this.isJumping = false;
    }

    if (onGround) {
      this.isJumping = false;
    }

    player.setVelocityX(0);

    if (this.tutorialStep === "intro") {
      return;
    }

    if (this.tutorialStep === "moveRight") {
      this.updateTutorialControlUI(this.tutorialUnlockedKeys);
      this.applyTutorialUnlockedMovement(player, walkSpeed, runSpeed);
      this.updateTutorialPlantHitbox();

      if (player.x - this.tutorialStartX >= this.tutorialMoveTarget) {
        this.showTutorialCongrats(this.tt("greatJob"));
        this.tutorialUnlockedKeys = ["right", "left"];

        this.setTutorialInstructions([
          this.tt("movedRight"),
          this.tt("moveLeft"),
        ]);

        this.setTutorialControls(this.tutorialUnlockedKeys);

        this.tutorialStep = "moveLeft";
        this.tutorialLeftDistance = 0;
        this.tutorialRunDistance = 0;
        this.tutorialGlideDistance = 0;
        this.tutorialPreviousX = this.tutorialPlayer.x;
        this.tutorialJumpStarted = false;
        this.tutorialHorizontalJumpStarted = false;
        this.tutorialRunTarget = 280;
        this.tutorialGlideTarget = 220;
        player.setVelocityX(0);
      }

      return;
    }

    if (this.tutorialStep === "moveLeft") {
      this.updateTutorialControlUI(this.tutorialUnlockedKeys);
      this.applyTutorialUnlockedMovement(player, walkSpeed, runSpeed);
      this.updateTutorialPlantHitbox();

      const deltaX = player.x - this.tutorialPreviousX;
      if (deltaX < 0) {
        this.tutorialLeftDistance += Math.abs(deltaX);
      }
      this.tutorialPreviousX = player.x;

      if (this.tutorialLeftDistance >= this.tutorialMoveTarget) {
        this.showTutorialCongrats(this.tt("nice"));

        this.tutorialUnlockedKeys = ["right", "left", "down"];

        this.setTutorialInstructions([
          this.tt("movedRight"),
          this.tt("movedLeft"),
          this.tt("crouch"),
        ]);

        this.setTutorialControls(this.tutorialUnlockedKeys);

        this.tutorialStep = "crouch";
        this.tutorialHoldTimer = 0;
        player.setVelocityX(0);
      }

      return;
    }

    if (this.tutorialStep === "crouch") {
      this.updateTutorialControlUI(this.tutorialUnlockedKeys);
      this.applyTutorialUnlockedMovement(player, walkSpeed, runSpeed);
      this.updateTutorialPlantHitbox();

      if (this.cursors.down.isDown && onGround) {
        player.setVelocityX(0);
        player.anims.stop();
        player.setTexture("crouchFinal");
        this.tutorialHoldTimer += this.game.loop.delta;
      } else if (!this.cursors.left.isDown && !this.cursors.right.isDown) {
        this.tutorialHoldTimer = 0;
      }

      if (this.tutorialHoldTimer >= this.tutorialCrouchTarget) {
        this.showTutorialCongrats(this.tt("good"));

        this.tutorialUnlockedKeys = ["right", "left", "down", "up"];

        this.setTutorialInstructions([
          this.tt("movedRight"),
          this.tt("movedLeft"),
          this.tt("crouched"),
          this.tt("holdUp"),
        ]);

        this.setTutorialControls(this.tutorialUnlockedKeys);

        this.tutorialStep = "holdUp";
        this.tutorialHoldTimer = 0;
      }

      return;
    }

    if (this.tutorialStep === "holdUp") {
      this.updateTutorialControlUI(this.tutorialUnlockedKeys);
      this.applyTutorialUnlockedMovement(player, walkSpeed, runSpeed);
      this.updateTutorialPlantHitbox();

      if (this.cursors.up.isDown && onGround) {
        player.setVelocityX(0);
        player.anims.stop();
        player.setTexture("up");
        this.tutorialHoldTimer += this.game.loop.delta;
      } else if (!this.cursors.left.isDown && !this.cursors.right.isDown) {
        this.tutorialHoldTimer = 0;
      }

      if (this.tutorialHoldTimer >= this.tutorialUpTarget) {
        this.showTutorialCongrats(this.tt("excellent"));

        this.tutorialUnlockedKeys = ["right", "left", "down", "up", "z"];

        this.setTutorialInstructions([
          this.tt("movedRight"),
          this.tt("movedLeft"),
          this.tt("crouched"),
          this.tt("heldPotUp"),
          this.tt("jump"),
        ]);

        this.setTutorialControls(this.tutorialUnlockedKeys);

        this.tutorialStep = "jump";
        this.tutorialJumpStarted = false;
        this.tutorialHoldTimer = 0;
      }

      return;
    }

    if (this.tutorialStep === "jump") {
      this.updateTutorialControlUI(this.tutorialUnlockedKeys);
      this.applyTutorialUnlockedMovement(player, walkSpeed, runSpeed);
      this.updateTutorialPlantHitbox();
      if (this.isJumping) {
        this.tutorialJumpStarted = true;
      }
      if (this.tutorialJumpStarted && onGround && player.body.velocity.y >= 0) {
        this.showTutorialCongrats(this.tt("niceJump"));

        this.setTutorialInstructions([
          this.tt("movedRight"),
          this.tt("movedLeft"),
          this.tt("crouched"),
          this.tt("heldPotUp"),
          this.tt("jumped"),
          this.tt("horizontalJump"),
        ]);

        this.tutorialStep = "horizontalJump";
        this.tutorialHorizontalJumpStarted = false;
        this.tutorialPreviousX = player.x;
      }

      return;
    }

    if (this.tutorialStep === "horizontalJump") {
      this.updateTutorialControlUI(this.tutorialUnlockedKeys);
      this.applyTutorialUnlockedMovement(player, walkSpeed, runSpeed);
      this.updateTutorialPlantHitbox();

      if (
        this.isJumping &&
        !onGround &&
        (this.cursors.left.isDown || this.cursors.right.isDown)
      ) {
        this.tutorialHorizontalJumpStarted = true;
      }

      const horizontalJumpDistance = Math.abs(
        player.x - this.tutorialPreviousX,
      );

      if (
        this.tutorialHorizontalJumpStarted &&
        horizontalJumpDistance >= this.tutorialMoveTarget
      ) {
        this.showTutorialCongrats(this.tt("greatDistance"));

        this.tutorialUnlockedKeys = ["right", "left", "down", "up", "z", "x"];

        this.setTutorialInstructions([
          this.tt("movedRight"),
          this.tt("movedLeft"),
          this.tt("crouched"),
          this.tt("heldPotUp"),
          this.tt("jumped"),
          this.tt("longJump"),
          this.tt("run"),
        ]);

        this.setTutorialControls(this.tutorialUnlockedKeys);

        this.tutorialStep = "run";
        this.tutorialRunDistance = 0;
        this.tutorialPreviousX = player.x;
      }

      return;
    }

    if (this.tutorialStep === "run") {
      this.updateTutorialControlUI(this.tutorialUnlockedKeys);
      this.applyTutorialUnlockedMovement(player, walkSpeed, runSpeed);
      this.updateTutorialPlantHitbox();

      const deltaX = Math.abs(player.x - this.tutorialPreviousX);

      if (
        this.diveKey.isDown &&
        onGround &&
        (this.cursors.left.isDown || this.cursors.right.isDown)
      ) {
        this.tutorialRunDistance += deltaX;
      }

      this.tutorialPreviousX = player.x;

      if (this.tutorialRunDistance >= this.tutorialRunTarget) {
        this.showTutorialCongrats(this.tt("fast"));

        this.setTutorialInstructions([
          this.tt("movedRight"),
          this.tt("movedLeft"),
          this.tt("crouched"),
          this.tt("heldPotUp"),
          this.tt("jumped"),
          this.tt("longJump"),
          this.tt("ran"),
          this.tt("glide"),
        ]);

        this.tutorialStep = "glide";
        this.tutorialGlideDistance = 0;
        this.tutorialPreviousX = player.x;
      }

      return;
    }

    if (this.tutorialStep === "glide") {
      this.updateTutorialControlUI(this.tutorialUnlockedKeys);
      this.applyTutorialUnlockedMovement(player, walkSpeed, runSpeed);
      this.updateTutorialPlantHitbox();

      const deltaX = Math.abs(player.x - this.tutorialPreviousX);

      if (
        this.diveKey.isDown &&
        !onGround &&
        (this.cursors.left.isDown || this.cursors.right.isDown)
      ) {
        this.tutorialGlideDistance += deltaX;
      }

      this.tutorialPreviousX = player.x;

      if (this.tutorialGlideDistance >= this.tutorialGlideTarget) {
        this.showTutorialCongrats(this.tt("smoothGlide"));

        this.spawnTutorialSpring();

        this.setTutorialInstructions([
          this.tt("movedRight"),
          this.tt("movedLeft"),
          this.tt("crouched"),
          this.tt("heldPotUp"),
          this.tt("jumped"),
          this.tt("longJump"),
          this.tt("ran"),
          this.tt("glided"),
          this.tt("springInstruction"),
        ]);

        this.tutorialStep = "springDive";
        this.tutorialGlideDistance = 0;
        this.tutorialSpringLaunched = false;
      }

      return;
    }

    if (this.tutorialStep === "airHoldUp") {
      this.updateTutorialControlUI(this.tutorialUnlockedKeys);
      this.applyTutorialUnlockedMovement(player, walkSpeed, runSpeed);
      this.updateTutorialPlantHitbox();

      if (this.cursors.up.isDown && !onGround) {
        this.tutorialAirHoldUpTimer += this.game.loop.delta;
      } else {
        this.tutorialAirHoldUpTimer = 0;
      }

      if (this.tutorialAirHoldUpTimer >= 250) {
        this.showTutorialCongrats(this.tt("potUp"));

        this.setTutorialInstructions([
          this.tt("movedRight"),
          this.tt("movedLeft"),
          this.tt("crouched"),
          this.tt("heldPotUp"),
          this.tt("jumped"),
          this.tt("longJump"),
          this.tt("ran"),
          this.tt("glided"),
          this.tt("springDive"),
          this.tt("airPotHold"),
          this.tt("catchDrops", { count: 0 }),
        ]);

        this.tutorialStep = "catchDrops";
        this.tutorialAirHoldUpTimer = 0;
        this.tutorialDropsCaught = 0;
        this.spawnTutorialDrops();
      }

      return;
    }

    if (this.tutorialStep === "springDive") {
      this.updateTutorialControlUI(this.tutorialUnlockedKeys);
      this.applyTutorialUnlockedMovement(player, walkSpeed, runSpeed);
      this.updateTutorialPlantHitbox();

      if (this.tutorialSpringLaunched && !onGround) {
        const deltaY = player.y - this.tutorialSpringPreviousY;

        if (this.cursors.down.isDown && deltaY > 0) {
          this.tutorialSpringDiveDistance += deltaY;
        }

        this.tutorialSpringPreviousY = player.y;
      }

      if (this.tutorialSpringDiveDistance >= this.tutorialSpringDiveTarget) {
        this.showTutorialCongrats(this.tt("springDiveMsg"));

        this.setTutorialInstructions([
          this.tt("movedRight"),
          this.tt("movedLeft"),
          this.tt("crouched"),
          this.tt("heldPotUp"),
          this.tt("jumped"),
          this.tt("longJump"),
          this.tt("ran"),
          this.tt("glided"),
          this.tt("springDive"),
          this.tt("airHoldInstruction"),
        ]);

        this.tutorialStep = "airHoldUp";
        this.tutorialSpringDiveDistance = 0;
        this.tutorialAirHoldUpTimer = 0;
      }

      return;
    }

    if (
      this.isJumping &&
      (Phaser.Input.Keyboard.JustUp(this.jumpKey) || mobileJumpReleased)
    ) {
      if (player.body.velocity.y < 0) {
        player.setVelocityY(player.body.velocity.y * 0.5);
      }
      this.isJumping = false;
    }

    if (onGround) {
      this.isDiving = false;
      this.isJumping = false;
      this.isPounding = false;
    }

    if (this.jumpKey.isDown && onGround) {
      player.setVelocityY(-300);
      this.isJumping = true;
    } else if (this.diveKey.isDown && !onGround) {
      this.isDiving = true;
      this.isPounding = false;

      player.anims.stop();
      player.setTexture("dive1");

      if (this.cursors.left.isDown) {
        player.setVelocityX(-runSpeed);
        player.setFlipX(false);
      } else if (this.cursors.right.isDown) {
        player.setVelocityX(runSpeed);
        player.setFlipX(true);
      }
    } else if (!onGround) {
      this.isDiving = false;
      this.isPounding = false;

      player.anims.stop();

      if (this.cursors.up.isDown) {
        player.setTexture("jumpup");
      } else {
        player.setTexture("jump1");
      }

      if (this.cursors.left.isDown) {
        player.setVelocityX(-walkSpeed);
        player.setFlipX(false);
      } else if (this.cursors.right.isDown) {
        player.setVelocityX(walkSpeed);
        player.setFlipX(true);
      }
    } else if (this.cursors.left.isDown) {
      if (this.diveKey.isDown) {
        player.setVelocityX(-runSpeed);
        player.anims.play("tutorialRun", true);
      } else {
        player.setVelocityX(-walkSpeed);
        player.anims.play("tutorialWalk", true);
      }

      player.setFlipX(false);
    } else if (this.cursors.right.isDown) {
      if (this.diveKey.isDown) {
        player.setVelocityX(runSpeed);
        player.anims.play("tutorialRun", true);
      } else {
        player.setVelocityX(walkSpeed);
        player.anims.play("tutorialWalk", true);
      }

      player.setFlipX(true);
    } else {
      player.anims.stop();

      if (onGround && this.cursors.down.isDown) {
        player.setTexture("crouchFinal");
      } else if (onGround && this.cursors.up.isDown) {
        player.setTexture("up");
      } else {
        player.setTexture("walk0");
      }
    }
  }
}
